import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { FaPlus, FaEye } from 'react-icons/fa';
import NoRecordFound from '../../../components/common/no-record-found';
import { formatDateDDMMYYEmpty } from '../../../components/common/dateUtils';

const ROWS_PER_PAGE = 20;

const SalesOrderList = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalQuantity, setTotalQuantity] = useState(0); // ✅ Grand total from backend
  const [showFilters, setShowFilters] = useState(false);
  
  const [filters, setFilters] = useState({
    invoiceNo: '',
    dateFrom: '',
    dateTo: '',
    buyer: '',
    commodity: '',
    vesselName: '',
    deliveryOrderNo: ''
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 400);
    return () => clearTimeout(handler);
  }, [filters]);

  const fetchData = async (page, searchValue, filterValues) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page,
        limit: ROWS_PER_PAGE,
        search: searchValue || '',
        invoiceNo: filterValues.invoiceNo || '',
        dateFrom: filterValues.dateFrom || '',
        dateTo: filterValues.dateTo || '',
        buyer: filterValues.buyer || '',
        commodity: filterValues.commodity || '',
        vesselName: filterValues.vesselName || '',
        deliveryOrderNo: filterValues.deliveryOrderNo || ''
      });
      const res = await fetch(`${API_BASE_URL}/api/sales/sales-order-get?${params.toString()}`, {
        method: 'GET',
        credentials: 'include',
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || `Server error: ${res.status}`);
      }
      
      const result = await res.json();
      setData(result.data || []);
      setTotal(result.total || 0);
      setTotalQuantity(result.totalQuantity || 0); // ✅ Set grand total
    } catch (err) {
      console.error('Error fetching sales orders:', err);
      setError(err.message || 'Failed to load data. Please try again.');
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(page, debouncedSearch, debouncedFilters);
  }, [page, debouncedSearch, debouncedFilters]);

  const totalPages = Math.ceil(total / ROWS_PER_PAGE);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters({
      invoiceNo: '',
      dateFrom: '',
      dateTo: '',
      buyer: '',
      commodity: '',
      vesselName: '',
      deliveryOrderNo: ''
    });
    setSearch('');
    setPage(1);
  };

  const handlePrev = () => setPage((p) => Math.max(1, p - 1));
  const handleNext = () => setPage((p) => Math.min(totalPages, p + 1));

  // ✅ Check if we're on the last page
  const isLastPage = page === totalPages;

  const formatNumber = (num) => {
    if (num === null || num === undefined || num === '') return '';
    return parseFloat(num).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatQuantity = (num) => {
    if (num === null || num === undefined || num === '') return '';
    return parseFloat(num).toLocaleString('en-IN', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    });
  };

  return (
    <div className="max-w-8xl mx-auto p-2 bg-white rounded border border-gray-100">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
        <h2 className="text-xl font-semibold">Sales Order Records</h2>
        <div className="flex gap-1 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Quick Search..."
            value={search}
            onChange={handleSearchChange}
            className="border border-gray-200 rounded px-2 py-1 focus:outline-none w-full sm:w-48 text-sm"
          />
          <button
            className={`flex items-center gap-1 px-3 py-1 text-xs rounded font-medium border ${
              showFilters 
                ? 'bg-gray-600 text-white border-gray-700 hover:bg-gray-700' 
                : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
            }`}
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          <button
            className="flex items-center gap-1 bg-[#3899cf] text-white px-3 py-1 text-xs rounded hover:bg-[#0e8dc6] border border-[#10a5e9] font-medium"
            onClick={() => navigate('/sales-order/new')}
          >
            <FaPlus />
            Add New
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="mb-4 p-3 bg-gray-50 rounded border border-gray-200">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Invoice No</label>
              <input
                type="text"
                placeholder="Enter Invoice No."
                value={filters.invoiceNo}
                onChange={(e) => handleFilterChange('invoiceNo', e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Date From</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Date To</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Buyer</label>
              <input
                type="text"
                placeholder="Search buyer..."
                value={filters.buyer}
                onChange={(e) => handleFilterChange('buyer', e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Commodity</label>
              <input
                type="text"
                placeholder="Search commodity..."
                value={filters.commodity}
                onChange={(e) => handleFilterChange('commodity', e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Vessel Name</label>
              <input
                type="text"
                placeholder="Search vessel..."
                value={filters.vesselName}
                onChange={(e) => handleFilterChange('vesselName', e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Delivery Order No</label>
              <input
                type="text"
                placeholder="Enter Delivery Order No..."
                value={filters.deliveryOrderNo}
                onChange={(e) => handleFilterChange('deliveryOrderNo', e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>
          <div className="mt-2 flex justify-end">
            <button
              onClick={handleResetFilters}
              className="px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 font-medium"
            >
              Reset All Filters
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center text-blue-600 py-6 text-sm">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-600 py-6 text-sm">{error}</div>
      ) : data.length === 0 ? (
        <NoRecordFound message="No sales orders found." />
      ) : (
        <div className="overflow-x-auto rounded border border-gray-50">
          <table className="min-w-full text-xs">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-2 py-2 text-left font-medium whitespace-nowrap">Invoice No</th>
                <th className="px-2 py-2 text-left font-medium whitespace-nowrap">Date</th>
                <th className="px-2 py-2 text-left font-medium whitespace-nowrap">Buyer</th>
                <th className="px-2 py-2 text-left font-medium whitespace-nowrap">Broker</th>
                <th className="px-2 py-2 text-left font-medium whitespace-nowrap">Warehouse</th>
                <th className="px-2 py-2 text-left font-medium whitespace-nowrap">Commodity</th>
                <th className="px-2 py-2 text-left font-medium whitespace-nowrap">Qty (MT)</th>
                <th className="px-2 py-2 text-left font-medium whitespace-nowrap">Price/MT</th>
                <th className="px-2 py-2 text-left font-medium whitespace-nowrap">Total</th>
                <th className="px-2 py-2 text-left font-medium whitespace-nowrap">Delivery Order</th>
                <th className="px-2 py-2 text-left font-medium whitespace-nowrap">Vehicle No</th>
                <th className="px-2 py-2 text-left font-medium whitespace-nowrap">Issuing Authority</th>
                <th className="px-2 py-2 text-left font-medium whitespace-nowrap">Vessel Name</th>
                <th className="px-2 py-2 text-left font-medium whitespace-nowrap">Origin Country</th>
                <th className="px-2 py-2 text-left font-medium whitespace-nowrap">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, idx) => {
                const totalValue = row.quantity_mt && row.sale_price_per_mt 
                  ? (parseFloat(row.quantity_mt) * parseFloat(row.sale_price_per_mt)).toFixed(2)
                  : '';
                
                return (
                  <tr key={row.sale_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-2 py-1 font-semibold text-blue-600 whitespace-nowrap">{row.invoice_no || '-'}</td>
                    <td className="px-2 py-1 whitespace-nowrap">{formatDateDDMMYYEmpty(row.order_date)}</td>
                    <td className="px-2 py-1 font-medium max-w-24 truncate" title={row.domestic_buyer_name}>{row.domestic_buyer_name}</td>
                    <td className="px-2 py-1 max-w-20 truncate" title={row.broker_name}>{row.broker_name || '-'}</td>
                    <td className="px-2 py-1 max-w-24 truncate" title={row.warehouse_name}>{row.warehouse_name}</td>
                    <td className="px-2 py-1 max-w-20 truncate" title={row.commodity_name}>{row.commodity_name}</td>
                    <td className="px-2 py-1 font-medium whitespace-nowrap">{formatQuantity(row.quantity_mt)}</td>
                    <td className="px-2 py-1 whitespace-nowrap">{formatNumber(row.sale_price_per_mt)}</td>
                    <td className="px-2 py-1 font-semibold text-green-700 whitespace-nowrap">{formatNumber(totalValue)}</td>
                    <td className="px-2 py-1 font-medium text-purple-600 whitespace-nowrap">{row.delivery_order_no || '-'}</td>
                    <td className="px-2 py-1 whitespace-nowrap">{row.vehicle_no || '-'}</td>
                    <td className="px-2 py-1 max-w-24 truncate" title={row.issuing_authority_name}>{row.issuing_authority_name || '-'}</td>
                    <td className="px-2 py-1 whitespace-nowrap">{row.vessel_name || '-'}</td>
                    <td className="px-2 py-1 whitespace-nowrap">{row.origin_country || '-'}</td>
                    <td className="px-2 py-1 whitespace-nowrap">
                      <button
                        onClick={() => navigate(`/sales-order/view/${row.sale_id}`)}
                        className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                        title="View Sale Order"
                      >
                        <FaEye />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {/* ✅ Show Grand Total ONLY on last page */}
            {isLastPage && (
              <tfoot className="bg-gray-100">
                <tr className="font-bold bg-gray-200">
                  <td colSpan="6" className="px-2 py-2 text-right">Grand Total Quantity:</td>
                  <td className="px-2 py-2 font-bold text-blue-700">{formatQuantity(parseFloat(totalQuantity))}</td>
                  <td colSpan="8"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {/* ✅ Delivery Order wali Pagination UI */}
      {total > ROWS_PER_PAGE && (
        <div className="flex justify-end items-center gap-2 mt-3 text-xs">
          <button
            onClick={handlePrev}
            disabled={page === 1}
            className={`px-2 py-1 rounded border transition-colors duration-100 ${
              page === 1 
                ? 'bg-gray-100 text-gray-300 border-gray-200' 
                : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'
            }`}
          >
            Prev
          </button>
          <span className="text-gray-600">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={handleNext}
            disabled={page === totalPages}
            className={`px-2 py-1 rounded border transition-colors duration-100 ${
              page === totalPages 
                ? 'bg-gray-100 text-gray-300 border-gray-200' 
                : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'
            }`}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default SalesOrderList;
